<template>
    <v-container fluid>
        <v-row justify="center">
            <v-col cols="12">
                <v-card width="100%">
                    <v-card-title class="d-block py-2 text-center font-weight-bold primary secondary--text" style="word-break: normal">
                        <v-icon color="secondary" class="mr-2">mdi-shield-key</v-icon>
                        BILLETERAS
                    </v-card-title>
                    <v-btn bottom color="secondary primary--text" :loading="loading || yieldLoading" depressed fab absolute right small @click="refreshAll">
                        <v-icon>mdi-sync</v-icon>
                    </v-btn>
                </v-card>
            </v-col>
        </v-row>

        <v-row justify="center">
            <v-col cols="12" xl="10">
                <v-card>
                    <v-progress-linear indeterminate fixed v-if="loading || yieldLoading" style="top:0"/>
                    <v-card-text>
                        <v-alert dense outlined type="info">
                            Active solo lo que ya este probado y listo para clientes.
                        </v-alert>

                        <v-alert
                            v-for="(warning, index) in custodyWarnings"
                            :key="'custody-warning-' + index"
                            dense
                            text
                            :type="warning.type">
                            {{ warning.text }}
                        </v-alert>

                        <v-row dense class="mb-2">
                            <v-col cols="12" sm="4">
                                <v-chip label color="primary" class="secondary--text font-weight-bold">
                                    {{ form.environment === 'production' ? 'Produccion' : 'Sandbox' }}
                                </v-chip>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-chip label :color="form.enabled ? 'success' : 'grey'" dark>
                                    {{ form.enabled ? 'Custodia activa' : 'Custodia apagada' }}
                                </v-chip>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-chip label color="secondary" class="primary--text font-weight-bold">
                                    {{ enabledAssetCount }} rutas activas
                                </v-chip>
                            </v-col>
                        </v-row>

                        <v-row dense>
                            <v-col v-for="section in custodySections" :key="section.key" cols="12" sm="6" lg="4">
                                <v-card outlined hover class="fill-height" @click="openConfig(section.key)">
                                    <v-list-item three-line>
                                        <v-list-item-avatar tile size="48" :color="section.color">
                                            <v-icon color="secondary" large>{{ section.icon }}</v-icon>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title class="subtitle-1 font-weight-bold">
                                                {{ section.title }}
                                            </v-list-item-title>
                                            <v-list-item-subtitle>
                                                {{ section.subtitle }}
                                            </v-list-item-subtitle>
                                            <div class="mt-2">
                                                <v-chip small label :color="section.statusColor" dark>
                                                    {{ section.status }}
                                                </v-chip>
                                            </div>
                                        </v-list-item-content>
                                        <v-list-item-action>
                                            <v-icon color="primary">mdi-chevron-right</v-icon>
                                        </v-list-item-action>
                                    </v-list-item>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="custodyDialog" scrollable max-width="1100px" persistent @input="handleCustodyDialogInput">
            <v-card class="custody-fixed-dialog">
                <v-progress-linear indeterminate fixed v-if="loading" style="top:0"/>
                <v-form ref="form" v-model="valid" @submit.prevent="submit" lazy-validation class="custody-fixed-form">
                    <v-card-title class="primary secondary--text custody-fixed-header" style="word-break: normal">
                        <v-icon color="secondary" class="mr-2">{{ activeCustodySection.icon }}</v-icon>
                        {{ activeCustodySection.title }}
                        <v-spacer/>
                        <v-btn icon color="secondary" @click="closeConfig">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-card-text class="custody-fixed-body pt-4">
                        <v-alert dense text type="info" v-if="activeCustodySection.notice">
                            {{ activeCustodySection.notice }}
                        </v-alert>

                        <template v-if="configDialog === 'general'">
                            <v-row>
                                <v-col cols="12" md="3">
                                    <v-switch v-model="form.enabled" label="Activar custodia" color="primary" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-switch v-model="form.allow_external_withdrawals" label="Retiros externos" color="primary" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-switch v-model="form.withdrawal_requires_admin" label="Requiere revision" color="primary" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-switch v-model="form.auto_submit_withdrawals" label="Enviar automatico" color="primary" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-select
                                        v-model="form.environment"
                                        :items="environments"
                                        label="Ambiente"
                                        prepend-icon="mdi-server"
                                        :rules="[rules.required('environment')]"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model.number="form.deposit_confirmations"
                                        label="Confirmaciones deposito"
                                        type="number"
                                        min="0"
                                        prepend-icon="mdi-counter"
                                        :rules="[rules.required('deposit_confirmations')]"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-select
                                        v-model="form.usdt_provider"
                                        :items="providers"
                                        label="Proveedor USDT"
                                        prepend-icon="mdi-currency-usd"
                                        :rules="[rules.required('usdt_provider')]"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-select
                                        v-model="form.usdc_provider"
                                        :items="providers"
                                        label="Proveedor USDC"
                                        prepend-icon="mdi-currency-usd"
                                        :rules="[rules.required('usdc_provider')]"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-select
                                        v-model="form.eurc_provider"
                                        :items="providers"
                                        label="Proveedor EURC"
                                        prepend-icon="mdi-currency-eur"
                                        :rules="[rules.required('eurc_provider')]"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12">
                                    <v-divider class="my-2"/>
                                    <div class="subtitle-1 font-weight-bold mb-2">Salida preferida</div>
                                </v-col>
                                <v-col v-for="asset in assets" :key="'preferred-network-' + asset" cols="12" md="4">
                                    <v-select
                                        v-model="form.preferred_networks[asset]"
                                        :items="preferredNetworkOptions(asset)"
                                        :label="'Red preferida ' + asset"
                                        prepend-icon="mdi-route"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12">
                                    <v-divider class="my-2"/>
                                    <div class="subtitle-1 font-weight-bold mb-2">Costos operativos</div>
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <v-text-field
                                        v-model.number="form.internal_transfer_fee_percent"
                                        label="Pago interno %"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        prepend-icon="mdi-percent"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <v-text-field
                                        v-model.number="form.internal_transfer_fee_fixed"
                                        label="Pago interno fijo"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        prepend-icon="mdi-cash-plus"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <v-text-field
                                        v-model.number="form.internal_exchange_fee_percent"
                                        label="Cambio interno %"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        prepend-icon="mdi-percent"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <v-text-field
                                        v-model.number="form.internal_exchange_fee_fixed"
                                        label="Cambio interno fijo"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        prepend-icon="mdi-swap-horizontal-circle"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <v-text-field
                                        v-model.number="form.crypto_withdrawal_fee_percent"
                                        label="Envio externo %"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        prepend-icon="mdi-percent-outline"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <v-text-field
                                        v-model.number="form.crypto_withdrawal_fee_fixed"
                                        label="Envio externo fijo"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        prepend-icon="mdi-wallet-plus"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12">
                                    <v-divider class="my-2"/>
                                    <div class="subtitle-1 font-weight-bold mb-2">Barridos operativos</div>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-switch v-model="form.sweep_enabled" label="Activar barridos a billetera maestra" color="primary" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-switch v-model="form.sweep_requires_review" label="Requiere revision del barrido" color="primary" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12">
                                    <v-alert dense outlined type="info">
                                        El cliente ve saldo por moneda. La red solo define por donde entra o sale la operacion.
                                    </v-alert>
                                </v-col>
                            </v-row>
                        </template>

                        <template v-else-if="configDialog === 'circle'">
                            <v-alert dense outlined type="info">
                                Complete Circle para recibir y enviar USDC/EURC.
                            </v-alert>
                            <v-alert dense outlined type="warning" v-if="form.circle_entity_secret">
                                Guarde este secreto fuera del sistema. Circle no lo muestra ni lo recupera despues.
                                <v-btn small text color="primary" class="ml-2" @click="copyText(form.circle_entity_secret)">
                                    <v-icon left small>mdi-content-copy</v-icon>Copiar
                                </v-btn>
                            </v-alert>
                            <v-alert dense outlined type="success" v-if="circleConnection.recovery_file">
                                Circle devolvio el archivo de recuperacion. Descarguelo y guardelo en un lugar seguro.
                                <v-btn small text color="primary" class="ml-2" @click="downloadCircleRecoveryFile">
                                    <v-icon left small>mdi-download</v-icon>Descargar
                                </v-btn>
                            </v-alert>
                            <v-row dense class="mb-2">
                                <v-col
                                    v-for="item in circleSetupItems"
                                    :key="'circle-setup-' + item.label"
                                    cols="12"
                                    sm="6"
                                    md="4">
                                    <v-chip label :color="item.ok ? 'success' : 'warning'" dark class="mr-1">
                                        <v-icon left small>{{ item.ok ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                                        {{ item.label }}
                                    </v-chip>
                                </v-col>
                            </v-row>
                            <v-row dense class="mb-2">
                                <v-col cols="12" md="4">
                                    <v-text-field v-model="circleWalletSetName" label="Nombre de billetera Circle" prepend-icon="mdi-format-title" :disabled="loading || circleConnecting"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-switch v-model="circleRegisterEntitySecret" label="Registrar secreto en Circle" color="primary" :disabled="loading || circleConnecting || !form.circle_entity_secret"/>
                                </v-col>
                                <v-col cols="12" md="4" class="d-flex flex-wrap align-center justify-end">
                                    <v-btn color="primary" outlined class="ma-1" :disabled="loading || circleConnecting" @click="generateCircleEntitySecret">
                                        <v-icon left>mdi-key-plus</v-icon>Generar secreto
                                    </v-btn>
                                    <v-btn color="primary" outlined class="ma-1" :loading="circlePublicKeyLoading" :disabled="loading || circleConnecting" @click="refreshCirclePublicKey">
                                        <v-icon left>mdi-key-sync</v-icon>Sincronizar llave
                                    </v-btn>
                                    <v-btn color="success" class="ma-1" :loading="circleConnecting" :disabled="loading" @click="connectCircle">
                                        <v-icon left>mdi-link-variant</v-icon>Conectar Circle
                                    </v-btn>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="form.circle_wallet_set_id" label="ID billetera Circle" prepend-icon="mdi-wallet-outline" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="form.circle_entity_id" label="ID entidad Circle" prepend-icon="mdi-domain" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field v-model="form.circle_api_base_url" label="URL API Circle" prepend-icon="mdi-web" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-select v-model="form.circle_account_type" :items="circleAccountTypes" label="Tipo de cuenta" prepend-icon="mdi-wallet" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="form.circle_api_key"
                                        :label="secretConfigured('circle_api_key') ? 'Clave API configurada' : 'Clave API'"
                                        prepend-icon="mdi-key"
                                        :append-icon="show.circle_api_key ? 'mdi-eye-off' : 'mdi-eye'"
                                        :type="show.circle_api_key ? 'text' : 'password'"
                                        :placeholder="secretConfigured('circle_api_key') ? 'Dejar vacio para conservar' : ''"
                                        :disabled="loading"
                                        @click:append="show.circle_api_key = !show.circle_api_key"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="form.circle_entity_secret"
                                        :label="secretConfigured('circle_entity_secret') ? 'Secreto configurado' : 'Secreto de Circle'"
                                        prepend-icon="mdi-lock-key"
                                        :append-icon="show.circle_entity_secret ? 'mdi-eye-off' : 'mdi-eye'"
                                        :type="show.circle_entity_secret ? 'text' : 'password'"
                                        :placeholder="secretConfigured('circle_entity_secret') ? 'Dejar vacio para conservar' : ''"
                                        :disabled="loading"
                                        @click:append="show.circle_entity_secret = !show.circle_entity_secret"/>
                                </v-col>
                                <v-col cols="12">
                                    <v-textarea
                                        v-model="form.circle_entity_public_key"
                                        label="Llave publica de Circle"
                                        hint="Necesaria para conectar Circle."
                                        persistent-hint
                                        prepend-icon="mdi-key-chain"
                                        rows="3"
                                        :disabled="loading"/>
                                </v-col>
                            </v-row>
                            <v-divider class="my-4"/>
                            <div class="subtitle-1 font-weight-bold mb-2">Avisos de Circle</div>
                            <v-alert dense outlined type="info">
                                Use la URL publica de produccion. Si ya existe, Valzum guarda la suscripcion encontrada.
                            </v-alert>
                            <v-row>
                                <v-col cols="12" md="8">
                                    <v-text-field
                                        v-model="form.circle_webhook_endpoint"
                                        label="URL de avisos en produccion"
                                        prepend-icon="mdi-link-variant"
                                        :disabled="loading || circleWebhookLoading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-switch
                                        v-model="form.circle_webhook_verify_signatures"
                                        label="Validar avisos Circle"
                                        color="primary"
                                        :disabled="loading || circleWebhookLoading"/>
                                </v-col>
                                <v-col cols="12" md="8">
                                    <v-combobox
                                        v-model="form.circle_webhook_notification_types"
                                        :items="circleNotificationOptions"
                                        label="Tipos de aviso"
                                        prepend-icon="mdi-bell-ring"
                                        multiple
                                        chips
                                        small-chips
                                        deletable-chips
                                        :disabled="loading || circleWebhookLoading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="form.circle_webhook_subscription_id"
                                        label="ID de suscripcion"
                                        prepend-icon="mdi-identifier"
                                        :disabled="loading || circleWebhookLoading"/>
                                </v-col>
                                <v-col cols="12" class="text-right">
                                    <v-btn
                                        color="success"
                                        :loading="circleWebhookLoading"
                                        :disabled="loading"
                                        @click="createCircleWebhook">
                                        <v-icon left>mdi-link-variant-plus</v-icon>Conectar avisos Circle
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </template>

                        <template v-else-if="configDialog === 'trongrid'">
                            <v-row dense class="mb-3">
                                <v-col
                                    v-for="item in tronGridSetupItems"
                                    :key="'trongrid-setup-' + item.label"
                                    cols="12"
                                    sm="6"
                                    md="3">
                                    <v-card outlined class="fill-height">
                                        <v-list-item>
                                            <v-list-item-avatar tile :color="item.ok ? 'success' : 'warning'">
                                                <v-icon color="white">{{ item.ok ? 'mdi-check' : 'mdi-alert-outline' }}</v-icon>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title class="font-weight-bold">{{ item.label }}</v-list-item-title>
                                                <v-list-item-subtitle>{{ item.text }}</v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="form.trongrid_api_key"
                                        :label="secretConfigured('trongrid_api_key') ? 'Clave API configurada' : 'Clave API'"
                                        hint="Se usa para consultar depositos USDT TRON con mayor estabilidad."
                                        persistent-hint
                                        prepend-icon="mdi-key"
                                        :append-icon="show.trongrid_api_key ? 'mdi-eye-off' : 'mdi-eye'"
                                        :type="show.trongrid_api_key ? 'text' : 'password'"
                                        :placeholder="secretConfigured('trongrid_api_key') ? 'Dejar vacio para conservar' : ''"
                                        :disabled="loading"
                                        @click:append="show.trongrid_api_key = !show.trongrid_api_key"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="form.trongrid_base_url"
                                        label="URL API TronGrid"
                                        prepend-icon="mdi-web"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="form.trongrid_usdt_trc20_contract"
                                        label="Contrato USDT"
                                        prepend-icon="mdi-file-key-outline"
                                        :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-switch
                                        v-model="form.trongrid_scan_enabled"
                                        label="Sincronizar depositos"
                                        color="primary"
                                        :disabled="loading"/>
                                </v-col>
                            </v-row>

                            <v-card outlined class="mt-2">
                                <v-card-text>
                                    <div class="subtitle-1 font-weight-bold mb-2">Operacion USDT TRON</div>
                                    <v-simple-table dense>
                                        <tbody>
                                        <tr>
                                            <td class="font-weight-bold" style="width: 180px">Recibir</td>
                                            <td>Valzum genera una direccion TRON por cliente y la escanea cada cinco minutos.</td>
                                        </tr>
                                        <tr>
                                            <td class="font-weight-bold">Abono</td>
                                            <td>Cuando llega USDT TRC20 se registra como deposito y queda en el flujo de verificacion.</td>
                                        </tr>
                                        <tr>
                                            <td class="font-weight-bold">Retirar</td>
                                            <td>El envio externo queda en revision de Pagador hasta aprobar la billetera operativa y firma de retiros.</td>
                                        </tr>
                                        <tr>
                                            <td class="font-weight-bold">Comision</td>
                                            <td>Las transferencias internas no pagan red; las externas cobran red TRON mas la comision Valzum configurada.</td>
                                        </tr>
                                        </tbody>
                                    </v-simple-table>
                                </v-card-text>
                            </v-card>
                        </template>

                        <template v-else-if="configDialog === 'tatum'">
                            <v-row dense class="mb-3">
                                <v-col cols="12" md="4">
                                    <v-card outlined class="fill-height">
                                        <v-list-item>
                                            <v-list-item-avatar tile color="primary">
                                                <v-icon color="secondary">mdi-alpha-t-circle</v-icon>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title class="font-weight-bold">Estado Tatum</v-list-item-title>
                                                <v-list-item-subtitle>
                                                    {{ tatumSetupReadyCount }} de {{ tatumSetupItems.length }} pasos listos
                                                </v-list-item-subtitle>
                                                <div class="mt-2">
                                                    <v-chip small label :color="tatumReady ? 'success' : 'warning'" dark>
                                                        {{ tatumReady ? 'Listo' : 'Pendiente' }}
                                                    </v-chip>
                                                </div>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-card>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-card outlined class="fill-height">
                                        <v-list-item>
                                            <v-list-item-avatar tile color="secondary">
                                                <v-icon color="primary">mdi-wallet-key</v-icon>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title class="font-weight-bold">Datos que se generan</v-list-item-title>
                                                <div class="body-2 text--secondary mt-1" style="white-space: normal">
                                                    Se genera lo necesario para pruebas controladas.
                                                </div>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-card>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-card outlined class="fill-height">
                                        <v-list-item>
                                            <v-list-item-avatar tile color="warning">
                                                <v-icon color="white">mdi-speedometer-slow</v-icon>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title class="font-weight-bold">Plan free</v-list-item-title>
                                                <div class="body-2 text--secondary mt-1" style="white-space: normal">
                                                    Use Tatum solo si necesita USDT Ethereum.
                                                </div>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <v-expansion-panels flat accordion class="mb-4">
                                <v-expansion-panel>
                                    <v-expansion-panel-header>
                                        <div class="d-flex align-center">
                                            <v-icon class="mr-2" color="primary">mdi-map-marker-path</v-icon>
                                            Datos necesarios
                                        </div>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <v-simple-table dense>
                                            <tbody>
                                            <tr>
                                                <td class="font-weight-bold">Clave API</td>
                                                <td>Se pega aqui y queda cifrada al guardar.</td>
                                            </tr>
                                            <tr>
                                                <td class="font-weight-bold">Llave de direcciones</td>
                                                <td>Valzum puede generarlo.</td>
                                            </tr>
                                            <tr>
                                                <td class="font-weight-bold">Firma de retiros</td>
                                                <td>Se usa para firmar retiros.</td>
                                            </tr>
                                            <tr>
                                                <td class="font-weight-bold">Clave de avisos</td>
                                                <td>
                                                    Lo define Valzum.
                                                    <v-btn x-small outlined color="primary" class="ml-2" @click="generateTatumWebhookSecret">
                                                        <v-icon left x-small>mdi-key-plus</v-icon>Generar clave
                                                    </v-btn>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </v-simple-table>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel>
                                    <v-expansion-panel-header>
                                        <div class="d-flex align-center">
                                            <v-icon class="mr-2" color="primary">mdi-information-outline</v-icon>
                                            Recomendacion inicial
                                        </div>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <v-list dense>
                                            <v-list-item>
                                                <v-list-item-icon><v-icon color="primary">mdi-numeric-1-circle</v-icon></v-list-item-icon>
                                                <v-list-item-content>
                                                    <div class="body-2" style="white-space: normal">Probar primero solo USDT por TRON.</div>
                                                </v-list-item-content>
                                            </v-list-item>
                                            <v-list-item>
                                                <v-list-item-icon><v-icon color="primary">mdi-numeric-2-circle</v-icon></v-list-item-icon>
                                                <v-list-item-content>
                                                    <div class="body-2" style="white-space: normal">No activar alertas masivas al inicio.</div>
                                                </v-list-item-content>
                                            </v-list-item>
                                            <v-list-item>
                                                <v-list-item-icon><v-icon color="primary">mdi-numeric-3-circle</v-icon></v-list-item-icon>
                                                <v-list-item-content>
                                                    <div class="body-2" style="white-space: normal">Escalar cuando el flujo ya este probado.</div>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>

                            <v-alert dense outlined type="success" v-if="tatumGeneratedWallet.xpub">
                                Llave {{ tatumGeneratedWallet.network }} generada y guardada.
                            </v-alert>
                            <v-alert dense outlined type="warning" v-if="tatumGeneratedWallet.mnemonic">
                                <div class="font-weight-bold mb-2">Frase de respaldo {{ tatumGeneratedWallet.network }}. Guardela ahora; no se almacena en Valzum.</div>
                                <v-textarea
                                    :value="tatumGeneratedWallet.mnemonic"
                                    readonly
                                    rows="2"
                                    hide-details
                                    class="mb-2"/>
                                <v-btn small text color="primary" @click="copyText(tatumGeneratedWallet.mnemonic)">
                                    <v-icon left small>mdi-content-copy</v-icon>Copiar frase
                                </v-btn>
                            </v-alert>

                            <div class="subtitle-1 font-weight-bold mb-2">Estado</div>
                            <v-row dense class="mb-2">
                                <v-col
                                    v-for="item in tatumSetupItems"
                                    :key="'tatum-setup-' + item.label"
                                    cols="12"
                                    sm="6"
                                    md="4">
                                    <v-card outlined class="pa-2 fill-height">
                                        <div class="d-flex align-center">
                                            <v-icon :color="item.ok ? 'success' : 'warning'" class="mr-2">
                                                {{ item.ok ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                            </v-icon>
                                            <span class="body-2 font-weight-medium">{{ item.label }}</span>
                                        </div>
                                    </v-card>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-select v-model="form.tatum_wallet_mode" :items="tatumWalletModes" label="Modo de billetera" prepend-icon="mdi-wallet" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field v-model="form.tatum_base_url" label="URL API Tatum" prepend-icon="mdi-web" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="form.tatum_api_key"
                                        :label="secretConfigured('tatum_api_key') ? 'Clave API configurada' : 'Clave API'"
                                        hint="Se toma desde Tatum."
                                        persistent-hint
                                        prepend-icon="mdi-key"
                                        :append-icon="show.tatum_api_key ? 'mdi-eye-off' : 'mdi-eye'"
                                        :type="show.tatum_api_key ? 'text' : 'password'"
                                        :placeholder="secretConfigured('tatum_api_key') ? 'Dejar vacio para conservar' : ''"
                                        :disabled="loading"
                                        @click:append="show.tatum_api_key = !show.tatum_api_key"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="form.tatum_webhook_secret"
                                        :label="secretConfigured('tatum_webhook_secret') ? 'Clave de avisos configurada' : 'Clave de avisos'"
                                        hint="La genera Valzum para validar avisos."
                                        persistent-hint
                                        prepend-icon="mdi-shield-key"
                                        :append-icon="show.tatum_webhook_secret ? 'mdi-eye-off' : 'mdi-eye'"
                                        :type="show.tatum_webhook_secret ? 'text' : 'password'"
                                        :placeholder="secretConfigured('tatum_webhook_secret') ? 'Dejar vacio para conservar' : ''"
                                        :disabled="loading"
                                        @click:append="show.tatum_webhook_secret = !show.tatum_webhook_secret"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="form.tatum_tron_xpub"
                                        label="Llave de direcciones TRON"
                                        hint="Se obtiene al generar la billetera TRON."
                                        persistent-hint
                                        prepend-icon="mdi-key-chain"
                                        :disabled="loading"/>
                                    <v-btn
                                        x-small
                                        outlined
                                        color="primary"
                                        class="mt-1"
                                        :loading="tatumWalletGenerating === 'TRX'"
                                        :disabled="loading || tatumWalletGenerating !== null"
                                        @click="generateTatumWallet('TRX')">
                                        <v-icon left small>mdi-wallet-plus</v-icon>Generar llave TRON
                                    </v-btn>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="form.tatum_tron_signature_id"
                                        :label="secretConfigured('tatum_tron_signature_id') ? 'Firma de retiros TRON configurada' : 'Firma de retiros TRON'"
                                        hint="Se usa para autorizar retiros."
                                        persistent-hint
                                        prepend-icon="mdi-shield-key"
                                        :append-icon="show.tatum_tron_signature_id ? 'mdi-eye-off' : 'mdi-eye'"
                                        :type="show.tatum_tron_signature_id ? 'text' : 'password'"
                                        :placeholder="secretConfigured('tatum_tron_signature_id') ? 'Dejar vacio para conservar' : ''"
                                        :disabled="loading"
                                        @click:append="show.tatum_tron_signature_id = !show.tatum_tron_signature_id"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field v-model.number="form.tatum_tron_fee_limit" label="Limite de comision TRON" prepend-icon="mdi-speedometer" type="number" min="1" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="form.tatum_eth_xpub"
                                        label="Llave de direcciones Ethereum"
                                        hint="Opcional si USDT se habilita por ERC20."
                                        persistent-hint
                                        prepend-icon="mdi-key-chain"
                                        :disabled="loading"/>
                                    <v-btn
                                        x-small
                                        outlined
                                        color="primary"
                                        class="mt-1"
                                        :loading="tatumWalletGenerating === 'ETH'"
                                        :disabled="loading || tatumWalletGenerating !== null"
                                        @click="generateTatumWallet('ETH')">
                                        <v-icon left small>mdi-wallet-plus</v-icon>Generar llave ETH
                                    </v-btn>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="form.tatum_eth_signature_id"
                                        :label="secretConfigured('tatum_eth_signature_id') ? 'Firma de retiros ETH configurada' : 'Firma de retiros ETH'"
                                        hint="Opcional para retiros USDT por ERC20."
                                        persistent-hint
                                        prepend-icon="mdi-shield-key"
                                        :append-icon="show.tatum_eth_signature_id ? 'mdi-eye-off' : 'mdi-eye'"
                                        :type="show.tatum_eth_signature_id ? 'text' : 'password'"
                                        :placeholder="secretConfigured('tatum_eth_signature_id') ? 'Dejar vacio para conservar' : ''"
                                        :disabled="loading"
                                        @click:append="show.tatum_eth_signature_id = !show.tatum_eth_signature_id"/>
                                </v-col>
                                <v-col cols="12" md="2">
                                    <v-text-field v-model.number="form.tatum_eth_gas_limit" label="Limite gas ETH" prepend-icon="mdi-gauge" type="number" min="21000" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="2">
                                    <v-text-field v-model.number="form.tatum_eth_gas_price_gwei" label="Precio gas ETH" prepend-icon="mdi-gas-station" type="number" min="1" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-switch
                                        v-model="tatumOverwriteXpub"
                                        label="Permitir sobrescribir llaves"
                                        color="warning"
                                        :disabled="loading || tatumWalletGenerating !== null"/>
                                </v-col>
                            </v-row>
                            <v-divider class="my-4"/>
                            <v-card outlined class="mb-4">
                                <v-card-text class="py-3">
                                    <div class="subtitle-1 font-weight-bold d-flex align-center">
                                        <v-icon color="primary" class="mr-2">mdi-webhook</v-icon>
                                        Avisos de Tatum
                                    </div>
                                    <div class="body-2 text--secondary mt-1">
                                        Configure solo si usara Tatum.
                                    </div>
                                </v-card-text>
                            </v-card>
                            <v-row>
                                <v-col cols="12" md="8">
                                    <v-text-field
                                        v-model="form.tatum_webhook_endpoint"
                                        label="URL de avisos en produccion"
                                        prepend-icon="mdi-link-variant"
                                        :disabled="loading || tatumWebhookLoading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-switch
                                        v-model="form.tatum_webhook_verify_hmac"
                                        label="Validar avisos Tatum"
                                        color="primary"
                                        :disabled="loading || tatumWebhookLoading"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="form.tatum_kms_completed_subscription_id"
                                        label="Aviso de retiro completado"
                                        prepend-icon="mdi-check-network"
                                        :disabled="loading || tatumWebhookLoading"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="form.tatum_kms_failed_subscription_id"
                                        label="Aviso de retiro fallido"
                                        prepend-icon="mdi-alert-circle"
                                        :disabled="loading || tatumWebhookLoading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-switch
                                        v-model="tatumCreateAddressSubscriptions"
                                        label="Avisos de depositos"
                                        color="primary"
                                        :disabled="loading || tatumWebhookLoading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-switch
                                        v-model="tatumRecreateSubscriptions"
                                        label="Recrear avisos"
                                        color="warning"
                                        :disabled="loading || tatumWebhookLoading"/>
                                </v-col>
                                <v-col cols="12" md="4" class="d-flex align-center justify-end">
                                    <v-chip label color="secondary" class="primary--text font-weight-bold">
                                        {{ tatumAddressSubscriptionCount }} direcciones con avisos
                                    </v-chip>
                                </v-col>
                                <v-col cols="12" class="text-right">
                                    <v-btn
                                        color="success"
                                        :loading="tatumWebhookLoading"
                                        :disabled="loading"
                                        @click="createTatumWebhook">
                                        <v-icon left>mdi-bell-check</v-icon>Configurar avisos
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </template>

                        <template v-else-if="configDialog === 'bitgo'">
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="form.bitgo_enterprise_id" label="Enterprise ID" prepend-icon="mdi-domain" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="form.bitgo_wallet_id" label="ID de billetera" prepend-icon="mdi-wallet" :disabled="loading"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="form.bitgo_access_token"
                                        :label="secretConfigured('bitgo_access_token') ? 'Token de acceso configurado' : 'Token de acceso'"
                                        prepend-icon="mdi-key"
                                        :append-icon="show.bitgo_access_token ? 'mdi-eye-off' : 'mdi-eye'"
                                        :type="show.bitgo_access_token ? 'text' : 'password'"
                                        :placeholder="secretConfigured('bitgo_access_token') ? 'Dejar vacio para conservar' : ''"
                                        :disabled="loading"
                                        @click:append="show.bitgo_access_token = !show.bitgo_access_token"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="form.bitgo_webhook_secret"
                                        :label="secretConfigured('bitgo_webhook_secret') ? 'Clave de avisos configurada' : 'Clave de avisos'"
                                        prepend-icon="mdi-shield-key"
                                        :append-icon="show.bitgo_webhook_secret ? 'mdi-eye-off' : 'mdi-eye'"
                                        :type="show.bitgo_webhook_secret ? 'text' : 'password'"
                                        :placeholder="secretConfigured('bitgo_webhook_secret') ? 'Dejar vacio para conservar' : ''"
                                        :disabled="loading"
                                        @click:append="show.bitgo_webhook_secret = !show.bitgo_webhook_secret"/>
                                </v-col>
                            </v-row>
                        </template>

                        <template v-else-if="configDialog === 'master'">
                            <div class="d-flex align-center mb-2">
                                <div class="subtitle-1 font-weight-bold">Billeteras maestras</div>
                                <v-spacer/>
                                <v-btn small color="primary" outlined :disabled="loading" @click="syncMasterWalletsWithAssets">
                                    <v-icon left small>mdi-sync</v-icon>Sincronizar rutas
                                </v-btn>
                            </div>
                            <v-alert dense outlined type="info">
                                Configure direcciones propias de Valzum para mover liquidez real. El saldo del cliente sigue unificado por moneda.
                            </v-alert>
                            <v-simple-table>
                                <thead>
                                <tr>
                                    <th>Moneda</th>
                                    <th>Red</th>
                                    <th>Proveedor</th>
                                    <th>Direccion maestra</th>
                                    <th>Tag/Memo</th>
                                    <th>Minimo</th>
                                    <th>Reserva</th>
                                    <th>Fee red</th>
                                    <th>Activa</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(wallet, index) in form.master_wallets" :key="'master-' + index">
                                    <td style="min-width: 110px">
                                        <v-select v-model="wallet.asset" :items="assets" dense hide-details :disabled="loading"/>
                                    </td>
                                    <td style="min-width: 120px">
                                        <v-text-field v-model="wallet.network" dense hide-details :disabled="loading"/>
                                    </td>
                                    <td style="min-width: 150px">
                                        <v-select v-model="wallet.provider" :items="providers" dense hide-details :disabled="loading"/>
                                    </td>
                                    <td style="min-width: 260px">
                                        <v-text-field v-model="wallet.address" dense hide-details :disabled="loading"/>
                                    </td>
                                    <td style="min-width: 120px">
                                        <v-text-field v-model="wallet.tag" dense hide-details :disabled="loading"/>
                                    </td>
                                    <td style="min-width: 120px">
                                        <v-text-field v-model.number="wallet.min_sweep_amount" type="number" min="0" step="0.01" dense hide-details :disabled="loading"/>
                                    </td>
                                    <td style="min-width: 120px">
                                        <v-text-field v-model.number="wallet.reserve_amount" type="number" min="0" step="0.01" dense hide-details :disabled="loading"/>
                                    </td>
                                    <td style="min-width: 120px">
                                        <v-text-field v-model.number="wallet.network_fee_estimate" type="number" min="0" step="0.01" dense hide-details :disabled="loading"/>
                                    </td>
                                    <td>
                                        <v-switch v-model="wallet.enabled" dense hide-details color="primary" :disabled="loading"/>
                                    </td>
                                </tr>
                                </tbody>
                            </v-simple-table>
                        </template>

                        <template v-else-if="configDialog === 'assets'">
                            <div class="d-flex align-center mb-2">
                                <div class="subtitle-1 font-weight-bold">Monedas y rutas</div>
                                <v-spacer/>
                                <v-btn small color="primary" outlined class="mr-2" :disabled="loading" @click="prepareRecommendedAssets">
                                    <v-icon left small>mdi-flash</v-icon>Preparar monedas
                                </v-btn>
                                <v-btn small color="primary" outlined :disabled="loading" @click="addAsset">
                                    <v-icon left small>mdi-plus</v-icon>Agregar
                                </v-btn>
                            </div>
                            <v-alert dense outlined type="info">
                                Preparar activa USDC. EURC y USDT quedan para activacion manual.
                            </v-alert>
                            <v-alert dense text type="warning" v-if="circleAssetsNeedTokenIds">
                                Falta el identificador de envio en Circle. Sin eso no se envia a billeteras externas.
                            </v-alert>
                            <v-row dense class="mb-3">
                                <v-col
                                    v-for="item in recommendedAssetStatuses"
                                    :key="'asset-status-' + item.label"
                                    cols="12"
                                    sm="6"
                                    md="4">
                                    <v-chip label :color="item.ok ? 'success' : 'warning'" dark>
                                        <v-icon left small>{{ item.ok ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                                        {{ item.label }}
                                    </v-chip>
                                </v-col>
                            </v-row>
                            <v-simple-table>
                                <thead>
                                <tr>
                                    <th>Moneda</th>
                                    <th>Red</th>
                                    <th>Proveedor</th>
                                    <th>ID de envio</th>
                                    <th>Reserva red</th>
                                    <th>Preferida</th>
                                    <th>Disponible</th>
                                    <th></th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(asset, index) in form.enabled_assets" :key="index">
                                    <td style="min-width: 110px">
                                        <v-select v-model="asset.asset" :items="assets" dense hide-details :disabled="loading"/>
                                    </td>
                                    <td style="min-width: 130px">
                                        <v-text-field v-model="asset.network" dense hide-details :disabled="loading"/>
                                    </td>
                                    <td style="min-width: 150px">
                                        <v-select v-model="asset.provider" :items="providers" dense hide-details :disabled="loading"/>
                                    </td>
                                    <td style="min-width: 160px">
                                        <v-text-field v-model="asset.external_asset_id" dense hide-details :disabled="loading"/>
                                    </td>
                                    <td style="min-width: 130px">
                                        <v-text-field v-model.number="asset.network_fee_estimate" type="number" min="0" step="0.01" dense hide-details :disabled="loading"/>
                                    </td>
                                    <td class="text-center">
                                        <v-icon color="success" v-if="isPreferredRoute(asset)">mdi-check-circle</v-icon>
                                        <v-icon color="grey" v-else>mdi-circle-outline</v-icon>
                                    </td>
                                    <td>
                                        <v-switch v-model="asset.enabled" dense hide-details color="primary" :disabled="loading"/>
                                    </td>
                                    <td class="text-right">
                                        <v-btn icon small color="error" :disabled="loading || form.enabled_assets.length <= 1" @click="removeAsset(index)">
                                            <v-icon small>mdi-delete</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                                </tbody>
                            </v-simple-table>
                        </template>
                    </v-card-text>
                    <v-divider/>
                    <v-card-actions class="custody-fixed-footer">
                        <v-btn color="error" text @click="closeConfig">
                            <v-icon>mdi-close-circle</v-icon>&nbsp;&nbsp;Cerrar
                        </v-btn>
                        <v-spacer/>
                        <v-btn type="submit" color="success" :loading="loading" :disabled="loading || !valid">
                            <v-icon>mdi-check-circle</v-icon>&nbsp;&nbsp;Guardar billeteras
                        </v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>

        <v-dialog v-model="yieldDialog" scrollable max-width="1100px" persistent>
            <v-card class="custody-fixed-dialog">
                <v-progress-linear indeterminate fixed v-if="yieldLoading" style="top:0"/>
                <v-form ref="yieldFormRef" v-model="yieldValid" @submit.prevent="submitYield" lazy-validation class="custody-fixed-form">
                    <v-card-title class="primary secondary--text custody-fixed-header" style="word-break: normal">
                        <v-icon color="secondary" class="mr-2">mdi-file-chart-check</v-icon>
                        Rendimientos y extractos
                        <v-spacer/>
                        <v-btn icon color="secondary" @click="yieldDialog = false">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-card-title>
                        <v-card-text class="custody-fixed-body">
                            <v-alert dense text type="warning">
                                Active rendimientos solo cuando el modelo legal, contable y operativo este aprobado.
                            </v-alert>

                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-switch v-model="yieldForm.enabled" label="Activar rendimientos" color="primary" :disabled="yieldLoading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-switch v-model="yieldForm.auto_apply_yield" label="Abonar automatico" color="primary" :disabled="yieldLoading"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-switch v-model="yieldForm.statement_email_enabled" label="Enviar extracto por correo" color="primary" :disabled="yieldLoading"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-select
                                        v-model="yieldForm.eligible_currencies"
                                        :items="stablecoins"
                                        label="Monedas elegibles"
                                        multiple
                                        chips
                                        prepend-icon="mdi-currency-usd"
                                        :rules="[rules.required('eligible_currencies')]"
                                        :disabled="yieldLoading"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="yieldForm.payout_label" label="Descripcion del abono" prepend-icon="mdi-format-title" :disabled="yieldLoading"/>
                                </v-col>
                            </v-row>

                            <v-simple-table>
                                <thead>
                                <tr>
                                    <th>Moneda</th>
                                    <th>Tasa mensual (%)</th>
                                    <th>Saldo minimo</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="currency in stablecoins" :key="'yield-' + currency">
                                    <td class="font-weight-medium">{{ currency }}</td>
                                    <td style="min-width: 180px">
                                        <v-text-field
                                            v-model.number="yieldForm.monthly_rate_percent[currency]"
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.000001"
                                            dense
                                            hide-details
                                            suffix="%"
                                            :disabled="yieldLoading || !yieldForm.eligible_currencies.includes(currency)"/>
                                    </td>
                                    <td style="min-width: 180px">
                                        <v-text-field
                                            v-model.number="yieldForm.minimum_balances[currency]"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            dense
                                            hide-details
                                            :suffix="currency"
                                            :disabled="yieldLoading || !yieldForm.eligible_currencies.includes(currency)"/>
                                    </td>
                                </tr>
                                </tbody>
                            </v-simple-table>

                            <v-textarea v-model="yieldForm.disclaimer" label="Texto para el extracto" rows="2" prepend-icon="mdi-text-box-outline" :disabled="yieldLoading"/>
                        </v-card-text>
                        <v-divider/>
                        <v-card-actions class="custody-fixed-footer">
                            <v-btn color="error" text @click="yieldDialog = false">
                                <v-icon>mdi-close-circle</v-icon>&nbsp;&nbsp;Cerrar
                            </v-btn>
                            <v-spacer/>
                            <v-btn type="submit" color="success" :loading="yieldLoading" :disabled="yieldLoading || !yieldValid">
                                <v-icon>mdi-check-circle</v-icon>&nbsp;&nbsp;Guardar rendimientos
                            </v-btn>
                        </v-card-actions>
                    </v-form>
                </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios';
import Form from "../../../helpers/form";

export default {
    name: "configCustody",
    mixins: [ Form ],
    props: ['user'],
    data: () => ({
        loading: true,
        yieldLoading: true,
        circleConnecting: false,
        circleWebhookLoading: false,
        circlePublicKeyLoading: false,
        tatumWebhookLoading: false,
        tatumWalletGenerating: null,
        custodyDialog: false,
        configDialog: null,
        yieldDialog: false,
        circleRegisterEntitySecret: true,
        tatumCreateAddressSubscriptions: true,
        tatumRecreateSubscriptions: false,
        tatumOverwriteXpub: false,
        circleWalletSetName: 'Valzum Wallets',
        circleConnection: {
            entity_secret: null,
            recovery_file: null,
            wallet_set_id: null,
            entity_id: null,
            public_key: null
        },
        tatumGeneratedWallet: {
            network: null,
            xpub: null,
            mnemonic: null
        },
        valid: true,
        yieldValid: true,
        settings: {
            secret_flags: {}
        },
        show: {
            circle_api_key: false,
            circle_entity_secret: false,
            circle_webhook_secret: false,
            trongrid_api_key: false,
            tatum_api_key: false,
            tatum_webhook_secret: false,
            tatum_tron_signature_id: false,
            tatum_eth_signature_id: false,
            bitgo_access_token: false,
            bitgo_webhook_secret: false
        },
        form: {
            enabled: false,
            environment: 'sandbox',
            allow_external_withdrawals: false,
            auto_submit_withdrawals: false,
            withdrawal_requires_admin: true,
            deposit_confirmations: 12,
            internal_transfer_fee_percent: 0,
            internal_transfer_fee_fixed: 0,
            internal_exchange_fee_percent: 0,
            internal_exchange_fee_fixed: 0,
            crypto_withdrawal_fee_percent: 0,
            crypto_withdrawal_fee_fixed: 0,
            sweep_enabled: false,
            sweep_requires_review: true,
            usdc_provider: 'circle',
            eurc_provider: 'circle',
            usdt_provider: 'trongrid',
            circle_wallet_set_id: '',
            circle_entity_id: '',
            circle_entity_public_key: '',
            circle_api_base_url: 'https://api.circle.com',
            circle_account_type: 'EOA',
            circle_api_key: '',
            circle_entity_secret: '',
            circle_webhook_secret: '',
            circle_webhook_endpoint: 'https://web.valzum.com/api/wallet/crypto/webhooks/circle',
            circle_webhook_subscription_id: '',
            circle_webhook_notification_types: ['transactions.*'],
            circle_webhook_verify_signatures: true,
            trongrid_api_key: '',
            trongrid_base_url: 'https://api.trongrid.io',
            trongrid_usdt_trc20_contract: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
            trongrid_scan_enabled: true,
            tatum_api_key: '',
            tatum_webhook_secret: '',
            tatum_base_url: 'https://api.tatum.io',
            tatum_wallet_mode: 'wallet_operations_kms',
            tatum_tron_xpub: '',
            tatum_tron_signature_id: '',
            tatum_tron_fee_limit: 100,
            tatum_eth_xpub: '',
            tatum_eth_signature_id: '',
            tatum_eth_gas_limit: 70000,
            tatum_eth_gas_price_gwei: 30,
            tatum_webhook_endpoint: 'https://web.valzum.com/api/wallet/crypto/webhooks/tatum',
            tatum_webhook_verify_hmac: false,
            tatum_kms_completed_subscription_id: '',
            tatum_kms_failed_subscription_id: '',
            tatum_address_subscription_ids: {},
            bitgo_access_token: '',
            bitgo_webhook_secret: '',
            bitgo_enterprise_id: '',
            bitgo_wallet_id: '',
            enabled_assets: [],
            master_wallets: [],
            preferred_networks: {
                USDC: 'BASE',
                EURC: 'BASE',
                USDT: 'TRX'
            }
        },
        yieldForm: {
            enabled: false,
            auto_apply_yield: false,
            statement_email_enabled: true,
            eligible_currencies: ['USDT', 'USDC', 'EURC'],
            monthly_rate_percent: {
                USDT: 0,
                USDC: 0,
                EURC: 0
            },
            minimum_balances: {
                USDT: 0,
                USDC: 0,
                EURC: 0
            },
            payout_label: 'Rendimiento mensual Valzum',
            disclaimer: ''
        },
        environments: [
            { text: 'Sandbox', value: 'sandbox' },
            { text: 'Produccion', value: 'production' }
        ],
        providers: [
            { text: 'Circle', value: 'circle' },
            { text: 'TronGrid', value: 'trongrid' },
            { text: 'Tatum', value: 'tatum' },
            { text: 'BitGo', value: 'bitgo' },
            { text: 'Manual / proveedor externo', value: 'manual' }
        ],
        tatumWalletModes: [
            { text: 'Billetera administrada', value: 'wallet_operations_kms' },
            { text: 'Billetera inteligente', value: 'smart_wallets_sdk' }
        ],
        circleAccountTypes: [
            { text: 'EOA', value: 'EOA' },
            { text: 'SCA', value: 'SCA' }
        ],
        circleNotificationOptions: [
            'transactions.*',
            'transactions.inbound',
            'transactions.outbound',
            'challenges.*'
        ],
        assets: ['USDC', 'EURC', 'USDT'],
        stablecoins: ['USDT', 'USDC', 'EURC']
    }),
    created() {
        this.refreshAll();
    },
    computed: {
        enabledAssetCount() {
            return (this.form.enabled_assets || []).filter(item => item.enabled).length;
        },
        activeMasterWalletCount() {
            return (this.form.master_wallets || []).filter(item => item.enabled && item.address).length;
        },
        circleReady() {
            return this.circleOperationalReady;
        },
        circleOperationalReady() {
            return this.circleApiReady
                && this.circleEntitySecretReady
                && !!this.form.circle_wallet_set_id
                && !!this.form.circle_webhook_subscription_id;
        },
        circleApiReady() {
            return this.secretConfigured('circle_api_key') || !!this.form.circle_api_key;
        },
        circleEntitySecretReady() {
            return this.secretConfigured('circle_entity_secret') || !!this.form.circle_entity_secret;
        },
        circleDepositReady() {
            return this.circleApiReady
                && this.circleEntitySecretReady
                && !!this.form.circle_entity_public_key
                && !!this.form.circle_wallet_set_id
                && !!this.form.circle_webhook_subscription_id;
        },
        tronGridApiReady() {
            return this.secretConfigured('trongrid_api_key') || !!this.form.trongrid_api_key;
        },
        tronGridContractReady() {
            return !!this.form.trongrid_usdt_trc20_contract;
        },
        tronGridReady() {
            return this.tronGridApiReady
                && !!this.form.trongrid_base_url
                && this.tronGridContractReady
                && !!this.form.trongrid_scan_enabled;
        },
        tatumApiReady() {
            return this.secretConfigured('tatum_api_key') || !!this.form.tatum_api_key;
        },
        tatumHmacReady() {
            return this.secretConfigured('tatum_webhook_secret') || !!this.form.tatum_webhook_secret;
        },
        tatumTronDepositReady() {
            return this.tatumApiReady && !!this.form.tatum_tron_xpub;
        },
        tatumEthDepositReady() {
            return this.tatumApiReady && !!this.form.tatum_eth_xpub;
        },
        tatumTronReady() {
            return !!this.form.tatum_tron_xpub
                && (this.secretConfigured('tatum_tron_signature_id') || !!this.form.tatum_tron_signature_id);
        },
        tatumEthReady() {
            return !!this.form.tatum_eth_xpub
                && (this.secretConfigured('tatum_eth_signature_id') || !!this.form.tatum_eth_signature_id);
        },
        circleSetupItems() {
            return [
                { label: 'Clave API', ok: this.circleApiReady },
                { label: 'Secreto', ok: this.circleEntitySecretReady },
                { label: 'Llave publica sincronizada', ok: !!this.form.circle_entity_public_key },
                { label: 'Billetera Circle', ok: !!this.form.circle_wallet_set_id },
                { label: 'Avisos', ok: !!this.form.circle_webhook_subscription_id },
                { label: 'Validacion activa', ok: !!this.form.circle_webhook_verify_signatures }
            ];
        },
        tatumSetupItems() {
            return [
                { label: 'Clave API', ok: this.tatumApiReady },
                { label: 'Clave de avisos', ok: this.tatumHmacReady && !!this.form.tatum_webhook_verify_hmac },
                { label: 'Llave TRON', ok: !!this.form.tatum_tron_xpub },
                { label: 'Firma TRON', ok: this.secretConfigured('tatum_tron_signature_id') || !!this.form.tatum_tron_signature_id },
                { label: 'Avisos de retiros', ok: !!this.form.tatum_kms_completed_subscription_id && !!this.form.tatum_kms_failed_subscription_id },
                { label: 'Direcciones', ok: this.tatumAddressSubscriptionCount > 0 || !this.tatumCreateAddressSubscriptions }
            ];
        },
        tronGridSetupItems() {
            return [
                { label: 'Clave API', text: 'Lectura estable de red', ok: this.tronGridApiReady },
                { label: 'URL API', text: 'Red principal o sandbox', ok: !!this.form.trongrid_base_url },
                { label: 'Contrato USDT', text: 'Contrato correcto', ok: this.tronGridContractReady },
                { label: 'Sincronizacion', text: 'Escaneo automatico activo', ok: !!this.form.trongrid_scan_enabled }
            ];
        },
        tatumSetupReadyCount() {
            return this.tatumSetupItems.filter(item => item.ok).length;
        },
        tatumAddressSubscriptionCount() {
            return Object.keys(this.form.tatum_address_subscription_ids || {}).length;
        },
        tatumReady() {
            return this.tatumApiReady
                && this.tatumHmacReady
                && (this.tatumTronReady || this.tatumEthReady);
        },
        recommendedAssetStatuses() {
            return [
                { label: 'USDC Circle', ok: this.circleDepositReady },
                { label: 'Polygon y Solana', ok: this.circleDepositReady },
                { label: 'USDT opcional', ok: this.tronGridReady || this.tatumEthDepositReady }
            ];
        },
        circleAssetsNeedTokenIds() {
            return !!this.form.allow_external_withdrawals
                && (this.form.enabled_assets || []).some(item => {
                    return item.enabled
                        && item.provider === 'circle'
                        && ['USDC', 'EURC'].indexOf(item.asset) !== -1
                        && !item.external_asset_id;
                });
        },
        custodySections() {
            return [
                {
                    key: 'general',
                    title: 'Operacion',
                    subtitle: 'Ambiente, revisiones y retiros.',
                    icon: 'mdi-tune',
                    color: 'primary',
                    status: this.form.enabled ? 'Activo' : 'Apagado',
                    statusColor: this.form.enabled ? 'success' : 'grey',
                    notice: 'Defina primero las reglas de uso y aprobacion.'
                },
                {
                    key: 'circle',
                    title: 'Circle',
                    subtitle: 'USDC y EURC.',
                    icon: 'mdi-alpha-c-circle',
                    color: 'primary',
                    status: this.circleReady ? 'Listo' : (this.circleApiReady ? 'Falta configurar' : 'Pendiente'),
                    statusColor: this.circleReady ? 'success' : 'warning',
                    notice: 'Use Circle para USDC y EURC. Sincronice si falta la llave publica.'
                },
                {
                    key: 'trongrid',
                    title: 'TronGrid',
                    subtitle: 'USDT en TRON.',
                    icon: 'mdi-transit-connection-horizontal',
                    color: 'primary',
                    status: this.tronGridReady ? 'Listo' : (this.tronGridApiReady ? 'Incompleto' : 'Pendiente'),
                    statusColor: this.tronGridReady ? 'success' : 'warning',
                    notice: 'Use TronGrid para recibir USDT TRON. Retiros automaticos quedan manuales.'
                },
                {
                    key: 'tatum',
                    title: 'Tatum opcional',
                    subtitle: 'USDT Ethereum si se necesita.',
                    icon: 'mdi-alpha-t-circle',
                    color: 'primary',
                    status: this.tatumReady ? 'Listo' : (this.tatumApiReady ? 'Incompleto' : 'Pendiente'),
                    statusColor: this.tatumReady ? 'success' : 'warning',
                    notice: 'Mantenga Tatum apagado si no se va a usar.'
                },
                {
                    key: 'bitgo',
                    title: 'BitGo',
                    subtitle: 'Proveedor opcional.',
                    icon: 'mdi-shield-key',
                    color: 'primary',
                    status: this.secretConfigured('bitgo_access_token') ? 'Credenciales' : 'Opcional',
                    statusColor: this.secretConfigured('bitgo_access_token') ? 'success' : 'grey',
                    notice: 'Configure BitGo solo si se va a usar.'
                },
                {
                    key: 'assets',
                    title: 'Activos y rutas',
                    subtitle: 'Monedas disponibles para clientes.',
                    icon: 'mdi-transit-connection-variant',
                    color: 'primary',
                    status: this.enabledAssetCount + ' activas',
                    statusColor: this.enabledAssetCount > 0 ? 'success' : 'warning',
                    notice: 'Active solo rutas listas para clientes.'
                },
                {
                    key: 'master',
                    title: 'Billeteras maestras',
                    subtitle: 'Destino para barridos.',
                    icon: 'mdi-wallet-check',
                    color: 'primary',
                    status: this.activeMasterWalletCount + ' listas',
                    statusColor: this.activeMasterWalletCount > 0 ? 'success' : 'warning',
                    notice: 'Use direcciones propias de Valzum y verifique la ruta antes de activar.'
                },
                {
                    key: 'yield',
                    title: 'Rendimientos',
                    subtitle: 'Tasas, minimos y extractos.',
                    icon: 'mdi-file-chart-check',
                    color: 'primary',
                    status: this.yieldForm.enabled ? 'Activo' : 'Apagado',
                    statusColor: this.yieldForm.enabled ? 'success' : 'grey'
                }
            ];
        },
        activeCustodySection() {
            return this.custodySections.find(section => section.key === this.configDialog) || {
                title: 'Billeteras digitales',
                icon: 'mdi-shield-key',
                notice: ''
            };
        },
        custodyWarnings() {
            const warnings = [];

            if (this.form.enabled && this.enabledAssetCount === 0) {
                warnings.push({
                    type: 'warning',
                    text: 'Active al menos una ruta antes de mostrar direcciones o retiros.'
                });
            }

            if (this.form.allow_external_withdrawals && !this.form.withdrawal_requires_admin) {
                warnings.push({
                    type: 'warning',
                    text: 'Los retiros externos quedaran aprobados al crearse. Revise limites y permisos.'
                });
            }

            if (this.form.auto_submit_withdrawals && !this.form.withdrawal_requires_admin) {
                warnings.push({
                    type: 'error',
                    text: 'Enviar automatico sin revision permite salida de fondos sin aprobacion.'
                });
            }

            return warnings;
        }
    },
    methods: {
        openConfig(key) {
            if (key === 'yield') {
                if (!this.yieldLoading) {
                    this.yieldDialog = true;
                }

                return;
            }

            if (this.loading) {
                return;
            }

            this.configDialog = key;
            this.custodyDialog = true;
        },
        closeConfig() {
            this.custodyDialog = false;
            this.configDialog = null;
        },
        handleCustodyDialogInput(open) {
            if (!open) {
                this.closeConfig();
            }
        },
        refreshAll() {
            this.getData();
            this.getYieldData();
        },
        getData() {
            this.loading = true;
            axios.get('/api/settings/wallet/custody').then(res => {
                this.applySettings(res.data.settings || {});
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible cargar billeteras.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.loading = false;
            });
        },
        applySettings(settings) {
            this.settings = settings;
            Object.keys(this.form).forEach(key => {
                if (this.isSecretField(key)) {
                    this.form[key] = '';
                } else if (Object.prototype.hasOwnProperty.call(settings, key)) {
                    this.form[key] = settings[key];
                }
            });
            this.form.enabled_assets = (settings.enabled_assets || []).map(item => Object.assign({}, item));
            this.form.master_wallets = (settings.master_wallets || []).map(item => Object.assign({}, item));
            this.form.preferred_networks = Object.assign({
                USDC: 'BASE',
                EURC: 'BASE',
                USDT: 'TRX'
            }, settings.preferred_networks || {});
        },
        isSecretField(key) {
            if (key === 'circle_entity_public_key') {
                return false;
            }

            return key.indexOf('_secret') !== -1
                || key.indexOf('signature_id') !== -1
                || key === 'bitgo_access_token'
                || key === 'circle_api_key'
                || key === 'trongrid_api_key'
                || key === 'tatum_api_key';
        },
        secretConfigured(key) {
            const flags = this.settings.secret_flags || {};
            return !!flags[key + '_configured'];
        },
        generateCircleEntitySecret() {
            const cryptoObject = window.crypto || window.msCrypto;

            if (!cryptoObject || typeof cryptoObject.getRandomValues !== 'function') {
                this.$toast.error('Este navegador no permite generar secretos de forma segura.', { icon: 'mdi-alert-circle' });
                return;
            }

            const bytes = new Uint8Array(32);
            cryptoObject.getRandomValues(bytes);
            this.form.circle_entity_secret = Array.prototype.map.call(bytes, value => {
                return ('0' + value.toString(16)).slice(-2);
            }).join('');
            this.show.circle_entity_secret = true;
            this.circleRegisterEntitySecret = true;
            this.circleConnection = Object.assign({}, this.circleConnection, {
                entity_secret: this.form.circle_entity_secret,
                recovery_file: null
            });
            this.$toast.info('Secreto generado. Guardelo antes de cerrar esta pantalla.', { icon: 'mdi-information' });
        },
        generateTatumWebhookSecret() {
            const cryptoObject = window.crypto || window.msCrypto;

            if (!cryptoObject || typeof cryptoObject.getRandomValues !== 'function') {
                this.$toast.error('Este navegador no permite generar secretos de forma segura.', { icon: 'mdi-alert-circle' });
                return;
            }

            const bytes = new Uint8Array(32);
            cryptoObject.getRandomValues(bytes);
            this.form.tatum_webhook_secret = Array.prototype.map.call(bytes, value => {
                return ('0' + value.toString(16)).slice(-2);
            }).join('');
            this.show.tatum_webhook_secret = true;
            this.form.tatum_webhook_verify_hmac = true;
            this.$toast.info('Clave generada. Guardela con Configurar avisos para que quede registrada.', { icon: 'mdi-information' });
        },
        generateTatumWallet(network) {
            if (!this.form.tatum_api_key && !this.secretConfigured('tatum_api_key')) {
                this.$toast.warning('Ingrese la clave API de Tatum para generar la llave.', { icon: 'mdi-alert-circle' });
                return;
            }

            this.tatumWalletGenerating = network;
            this.tatumGeneratedWallet = {
                network: null,
                xpub: null,
                mnemonic: null
            };

            const payload = Object.assign({}, this.form, {
                network: network,
                tatum_overwrite_xpub: this.tatumOverwriteXpub
            });

            axios.post('/api/settings/wallet/tatum/wallet', payload).then(res => {
                const wallet = res.data.generated_wallet || {};
                if (res.data.v === 'warning') {
                    this.$toast.warning(res.data.status, { icon: 'mdi-alert-circle' });
                } else {
                    this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                }

                this.applySettings(res.data.settings || {});

                if (wallet.network === 'TRX' && wallet.xpub) {
                    this.form.tatum_tron_xpub = wallet.xpub;
                }

                if (wallet.network === 'ETH' && wallet.xpub) {
                    this.form.tatum_eth_xpub = wallet.xpub;
                }

                this.tatumGeneratedWallet = {
                    network: wallet.network || network,
                    xpub: wallet.xpub || null,
                    mnemonic: wallet.mnemonic || null
                };
            }).catch(err => {
                this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                this.$toast.error(this.errorMessage(err, 'No fue posible generar la llave de Tatum.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.tatumWalletGenerating = null;
            });
        },
        connectCircle() {
            if (!this.form.circle_api_key && !this.secretConfigured('circle_api_key')) {
                this.$toast.warning('Ingrese la clave API de Circle para conectar la cuenta.', { icon: 'mdi-alert-circle' });
                return;
            }

            if (!this.form.circle_entity_secret && !this.secretConfigured('circle_entity_secret')) {
                this.generateCircleEntitySecret();
            }

            const visibleEntitySecret = this.form.circle_entity_secret;
            const payload = Object.assign({}, this.form, {
                circle_wallet_set_name: this.circleWalletSetName,
                register_entity_secret: this.circleRegisterEntitySecret && !!visibleEntitySecret,
                create_wallet_set: true
            });

            this.circleConnecting = true;
            axios.post('/api/settings/wallet/circle/connect', payload).then(res => {
                const connection = res.data.connection || {};
                this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                this.applySettings(res.data.settings || {});
                this.circleConnection = Object.assign({}, connection);

                if (visibleEntitySecret) {
                    this.form.circle_entity_secret = visibleEntitySecret;
                    this.show.circle_entity_secret = true;
                    this.circleConnection.entity_secret = visibleEntitySecret;
                } else if (connection.entity_secret) {
                    this.form.circle_entity_secret = connection.entity_secret;
                    this.show.circle_entity_secret = true;
                    this.circleConnection.entity_secret = connection.entity_secret;
                }

                if (connection.wallet_set_id) {
                    this.form.circle_wallet_set_id = connection.wallet_set_id;
                }

                if (connection.entity_id) {
                    this.form.circle_entity_id = connection.entity_id;
                }

                if (connection.public_key) {
                    this.form.circle_entity_public_key = connection.public_key;
                }
            }).catch(err => {
                this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                this.$toast.error(this.errorMessage(err, 'No fue posible conectar Circle.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.circleConnecting = false;
            });
        },
        refreshCirclePublicKey() {
            if (!this.form.circle_api_key && !this.secretConfigured('circle_api_key')) {
                this.$toast.warning('Ingrese la clave API de Circle para sincronizar la llave.', { icon: 'mdi-alert-circle' });
                return;
            }

            this.circlePublicKeyLoading = true;
            axios.post('/api/settings/wallet/circle/public-key', this.form).then(res => {
                const connection = res.data.connection || {};
                this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                this.applySettings(res.data.settings || {});

                if (connection.public_key) {
                    this.form.circle_entity_public_key = connection.public_key;
                }

                if (connection.entity_id) {
                    this.form.circle_entity_id = connection.entity_id;
                }
            }).catch(err => {
                this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                this.$toast.error(this.errorMessage(err, 'No fue posible sincronizar la llave publica de Circle.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.circlePublicKeyLoading = false;
            });
        },
        createCircleWebhook() {
            if (!this.form.circle_api_key && !this.secretConfigured('circle_api_key')) {
                this.$toast.warning('Ingrese la clave API de Circle para conectar los avisos.', { icon: 'mdi-alert-circle' });
                return;
            }

            if (!this.form.circle_webhook_endpoint) {
                this.form.circle_webhook_endpoint = 'https://web.valzum.com/api/wallet/crypto/webhooks/circle';
            }

            this.circleWebhookLoading = true;
            axios.post('/api/settings/wallet/circle/webhook', this.form).then(res => {
                const subscription = res.data.subscription || {};
                if (res.data.v === 'warning') {
                    this.$toast.warning(res.data.status, { icon: 'mdi-alert-circle' });
                } else {
                    this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                }
                this.applySettings(res.data.settings || {});

                if (subscription.id) {
                    this.form.circle_webhook_subscription_id = subscription.id;
                }
            }).catch(err => {
                this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                this.$toast.error(this.errorMessage(err, 'No fue posible conectar los avisos de Circle.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.circleWebhookLoading = false;
            });
        },
        createTatumWebhook() {
            if (!this.form.tatum_api_key && !this.secretConfigured('tatum_api_key')) {
                this.$toast.warning('Ingrese la clave API de Tatum para configurar los avisos.', { icon: 'mdi-alert-circle' });
                return;
            }

            if (!this.form.tatum_webhook_endpoint) {
                this.form.tatum_webhook_endpoint = 'https://web.valzum.com/api/wallet/crypto/webhooks/tatum';
            }

            const payload = Object.assign({}, this.form, {
                tatum_create_address_subscriptions: this.tatumCreateAddressSubscriptions,
                tatum_recreate_subscriptions: this.tatumRecreateSubscriptions
            });

            this.tatumWebhookLoading = true;
            axios.post('/api/settings/wallet/tatum/webhook', payload).then(res => {
                const setup = res.data.setup || {};
                if (res.data.v === 'warning') {
                    this.$toast.warning(res.data.status, { icon: 'mdi-alert-circle' });
                } else {
                    this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                }
                this.applySettings(res.data.settings || {});

                if (setup.webhook_secret) {
                    this.form.tatum_webhook_secret = setup.webhook_secret;
                    this.show.tatum_webhook_secret = true;
                }

                if (setup.kms_completed && setup.kms_completed.id) {
                    this.form.tatum_kms_completed_subscription_id = setup.kms_completed.id;
                }

                if (setup.kms_failed && setup.kms_failed.id) {
                    this.form.tatum_kms_failed_subscription_id = setup.kms_failed.id;
                }
            }).catch(err => {
                this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                this.$toast.error(this.errorMessage(err, 'No fue posible configurar Tatum.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.tatumWebhookLoading = false;
            });
        },
        copyText(value) {
            if (!value) return;

            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(value).then(() => {
                    this.$toast.success('Copiado.', { icon: 'mdi-check-circle' });
                });
                return;
            }

            const input = document.createElement('textarea');
            input.value = value;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
            this.$toast.success('Copiado.', { icon: 'mdi-check-circle' });
        },
        downloadCircleRecoveryFile() {
            if (!this.circleConnection.recovery_file) return;

            const blob = new Blob([this.circleConnection.recovery_file], { type: 'application/octet-stream' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.href = url;
            link.download = 'circle-recovery-file.dat';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        },
        addAsset() {
            this.form.enabled_assets.push({
                asset: 'USDT',
                network: 'TRX',
                provider: this.form.usdt_provider || 'trongrid',
                enabled: false,
                external_asset_id: '',
                network_fee_estimate: 0
            });
        },
        removeAsset(index) {
            this.form.enabled_assets.splice(index, 1);
        },
        syncMasterWalletsWithAssets() {
            const existing = {};
            (this.form.master_wallets || []).forEach(wallet => {
                existing[this.assetNetworkKey(wallet)] = true;
            });

            let added = 0;
            (this.form.enabled_assets || []).forEach(asset => {
                const key = this.assetNetworkKey(asset);
                if (existing[key]) {
                    return;
                }

                this.form.master_wallets.push({
                    asset: String(asset.asset || '').toUpperCase(),
                    network: String(asset.network || '').toUpperCase(),
                    provider: asset.provider || 'manual',
                    enabled: false,
                    address: '',
                    tag: '',
                    min_sweep_amount: 0,
                    reserve_amount: 0,
                    network_fee_estimate: Number(asset.network_fee_estimate || 0)
                });
                existing[key] = true;
                added += 1;
            });

            if (added > 0) {
                this.$toast.success('Rutas agregadas a billeteras maestras. Guarda para aplicar.', { icon: 'mdi-check-circle' });
            } else {
                this.$toast.info('Las rutas ya estan sincronizadas.', { icon: 'mdi-information' });
            }
        },
        assetNetworkKey(item) {
            let network = String(item.network || '').toUpperCase();
            if (network === 'POLYGON') network = 'MATIC';
            if (network === 'TRON') network = 'TRX';
            return String(item.asset || '').toUpperCase() + '|' + network;
        },
        preferredNetworkOptions(asset) {
            const selectedAsset = String(asset || '').toUpperCase();
            const networks = [];

            (this.form.enabled_assets || []).forEach(item => {
                if (String(item.asset || '').toUpperCase() !== selectedAsset) {
                    return;
                }

                let network = String(item.network || '').toUpperCase();
                if (network === 'POLYGON') network = 'MATIC';
                if (network === 'TRON') network = 'TRX';

                if (network && networks.indexOf(network) === -1) {
                    networks.push(network);
                }
            });

            const fallback = {
                USDC: ['BASE', 'MATIC', 'SOL', 'ETH'],
                EURC: ['BASE', 'ETH', 'AVAX', 'SOL'],
                USDT: ['TRX', 'ETH']
            };

            return networks.length ? networks : (fallback[selectedAsset] || []);
        },
        isPreferredRoute(item) {
            const asset = String(item.asset || '').toUpperCase();
            let network = String(item.network || '').toUpperCase();
            if (network === 'POLYGON') network = 'MATIC';
            if (network === 'TRON') network = 'TRX';

            return !!asset && !!network && String((this.form.preferred_networks || {})[asset] || '').toUpperCase() === network;
        },
        prepareRecommendedAssets() {
            let changed = 0;

            this.form.enabled = true;
            this.form.enabled_assets = (this.form.enabled_assets || []).map(item => {
                const asset = Object.assign({}, item, {
                    asset: String(item.asset || '').toUpperCase(),
                    network: String(item.network || '').toUpperCase(),
                    provider: item.provider || 'manual'
                });
                let shouldEnable = false;

                if (asset.asset === 'USDC' && this.circleDepositReady) {
                    shouldEnable = true;
                    asset.provider = 'circle';
                }

                if (shouldEnable && !asset.enabled) {
                    changed += 1;
                }

                asset.enabled = shouldEnable ? true : asset.enabled;

                return asset;
            });

            if (changed > 0) {
                this.$toast.success('USDC inicial preparado. Guarda para aplicar la configuracion.', { icon: 'mdi-check-circle' });
            } else {
                this.$toast.info('No hay nuevas monedas listas para preparar. Revisa el estado de Circle, TronGrid y Tatum.', { icon: 'mdi-information' });
            }
        },
        submit() {
            if (!this.$refs.form.validate()) return;

            this.loading = true;
            axios.post('/api/settings/wallet/custody', this.form).then(res => {
                this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                this.applySettings(res.data.settings || {});
            }).catch(err => {
                this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                this.$toast.error(this.errorMessage(err, 'No fue posible guardar billeteras.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.loading = false;
            });
        },
        getYieldData() {
            this.yieldLoading = true;
            axios.get('/api/settings/wallet/yield').then(res => {
                this.applyYieldSettings(res.data.settings || {});
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible cargar rendimientos.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.yieldLoading = false;
            });
        },
        applyYieldSettings(settings) {
            const rateDefaults = { USDT: 0, USDC: 0, EURC: 0 };
            const minimumDefaults = { USDT: 0, USDC: 0, EURC: 0 };

            this.yieldForm = Object.assign({}, this.yieldForm, {
                enabled: !!settings.enabled,
                auto_apply_yield: !!settings.auto_apply_yield,
                statement_email_enabled: settings.statement_email_enabled !== false,
                eligible_currencies: settings.eligible_currencies || ['USDT', 'USDC', 'EURC'],
                monthly_rate_percent: Object.assign({}, rateDefaults, settings.monthly_rate_percent || {}),
                minimum_balances: Object.assign({}, minimumDefaults, settings.minimum_balances || {}),
                payout_label: settings.payout_label || 'Rendimiento mensual Valzum',
                disclaimer: settings.disclaimer || ''
            });
        },
        submitYield() {
            if (!this.$refs.yieldFormRef.validate()) return;

            this.yieldLoading = true;
            axios.post('/api/settings/wallet/yield', this.yieldForm).then(res => {
                this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                this.applyYieldSettings(res.data.settings || {});
            }).catch(err => {
                this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                this.$toast.error(this.errorMessage(err, 'No fue posible guardar rendimientos.'), { icon: 'mdi-alert-circle' });
            }).then(() => {
                this.yieldLoading = false;
            });
        },
        errorMessage(err, fallback) {
            if (err.response && err.response.data && err.response.data.status) {
                return err.response.data.status;
            }

            return fallback;
        }
    }
}
</script>

<style scoped>
.custody-fixed-dialog {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.custody-fixed-form {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.custody-fixed-header,
.custody-fixed-footer {
    flex: 0 0 auto;
}

.custody-fixed-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}
</style>
